import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { LoadingProvider } from './contexts/LoadingContext'
import ToastContainer from './components/Toast'
import ProtectedRoute from './components/ProtectedRoute'
import SplashScreen from './components/SplashScreen'
import useVersionCheck from './hooks/useVersionCheck'

// Auth pages
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'

// Layout
import AdminClient from './pages/AdminClient'

// Internal pages
import DashboardPage from './pages/AdminClient/pages/DashboardPage'
import VotersPage from './pages/AdminClient/pages/VotersPage'
import DemandasPage from './pages/AdminClient/pages/DemandasPage'
import LeadersPage from './pages/AdminClient/pages/LeadersPage'
import LeaderDetailPage from './pages/AdminClient/pages/LeaderDetailPage'
import AgendaPage from './pages/AdminClient/pages/AgendaPage'
import RegioesPage from './pages/AdminClient/pages/RegioesPage'
import ConfiguracoesPage from './pages/AdminClient/pages/ConfiguracoesPage'
import VoterFormConfigPage from './pages/AdminClient/pages/VoterFormConfigPage'
import CampaignsPage from './pages/AdminClient/pages/CampaignsPage'
import IntegrationsPage from './pages/AdminClient/pages/IntegrationsPage'
import ReportsMapPage from './pages/AdminClient/pages/ReportsMapPage'
import LeadersMapPage from './pages/AdminClient/pages/LeadersMapPage'
import ApiDocsPage from './pages/AdminClient/pages/ApiDocsPage'
import ReportsOverviewPage from './pages/AdminClient/pages/ReportsOverviewPage'
import ReportsLeadersPage from './pages/AdminClient/pages/ReportsLeadersPage'
import VotersMapPage from './pages/AdminClient/pages/VotersMapPage'
import SectionsMapPage from './pages/AdminClient/pages/SectionsMapPage'
import PoliticalConfigPage from './pages/AdminClient/pages/PoliticalConfigPage'
import VoterDetailPage from './pages/AdminClient/pages/VoterDetailPage'
import UsersPage from './pages/AdminClient/pages/UsersPage'
import CategoriesPage from './pages/AdminClient/pages/CategoriesPage'
import DemandSettingsPage from './pages/AdminClient/pages/DemandSettingsPage'
import DemandDetailPage from './pages/AdminClient/pages/DemandDetailPage'
import DemandPipelineDetailPage from './pages/AdminClient/pages/DemandPipelineDetailPage'
import BlogPostsPage from './pages/AdminClient/pages/BlogPostsPage'
import BlogPostEditorPage from './pages/AdminClient/pages/BlogPostEditorPage'
import BlogCategoriesPage from './pages/AdminClient/pages/BlogCategoriesPage'
import BlogTagsPage from './pages/AdminClient/pages/BlogTagsPage'
import BlogMediaPage from './pages/AdminClient/pages/BlogMediaPage'
import LeaderDashboardPage from './pages/AdminClient/pages/LeaderDashboardPage'
import DigitadorDashboardPage from './pages/AdminClient/pages/DigitadorDashboardPage'
import ElectionDashboardPage from './pages/AdminClient/pages/ElectionDashboardPage'
import AppConfigPage from './pages/AdminClient/pages/AppConfigPage'
import AppConfigFormPage from './pages/AdminClient/pages/AppConfigFormPage'
import PushNotificationsPage from './pages/AdminClient/pages/PushNotificationsPage'
import CommunityDashboardPage from './pages/AdminClient/pages/CommunityDashboardPage'
import CommunityArsenalPage from './pages/AdminClient/pages/CommunityArsenalPage'
import CommunityMissionsPage from './pages/AdminClient/pages/CommunityMissionsPage'
import CommunityChannelsPage from './pages/AdminClient/pages/CommunityChannelsPage'
import CommunityMembersPage from './pages/AdminClient/pages/CommunityMembersPage'
import CommunitySettingsPage from './pages/AdminClient/pages/CommunitySettingsPage'
import CommunityChannelChatPage from './pages/AdminClient/pages/CommunityChannelChatPage'
import CommunityModerationPage from './pages/AdminClient/pages/CommunityModerationPage'

function AppContent() {
  const { isLoading, isSwitching, token } = useAuth()
  useVersionCheck()

  return (
    <>
      <SplashScreen show={(isLoading && !!token) || isSwitching} />
      <ToastContainer />
      <Routes>
        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <AdminClient />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* Political routes */}
          <Route path="contacts" element={<VotersPage key="contacts" fixedView="contacts" />} />
          <Route path="contacts/:id" element={<VoterDetailPage />} />
          <Route path="voters" element={<VotersPage key="voters" fixedView="voters" />} />
          <Route path="voters/:id" element={<VoterDetailPage />} />
          <Route path="demands" element={<DemandasPage />} />
          <Route path="demands/:id" element={<DemandDetailPage />} />
          <Route path="leaders" element={<LeadersPage />} />
          <Route path="leaders/:id" element={<LeaderDetailPage />} />
          <Route path="agenda" element={<AgendaPage />} />
          <Route path="regions" element={<RegioesPage />} />
          <Route path="communication/messages" element={<div className="text-gray-500">Mensagens - Em breve</div>} />
          <Route path="communication/tags" element={<div className="text-gray-500">Etiquetas - Em breve</div>} />
          <Route path="reports/overview" element={<ReportsOverviewPage />} />
          <Route path="reports/leaders" element={<ReportsLeadersPage />} />
          <Route path="reports/map" element={<ReportsMapPage />} />
          <Route path="reports/map/voters" element={<VotersMapPage />} />
          <Route path="reports/map/leaders" element={<LeadersMapPage />} />
          <Route path="reports/map/sections" element={<SectionsMapPage />} />
          <Route path="reports/elections" element={<ElectionDashboardPage />} />

          {/* Blog */}
          <Route path="blog/posts" element={<BlogPostsPage />} />
          <Route path="blog/posts/new" element={<BlogPostEditorPage />} />
          <Route path="blog/posts/:id/edit" element={<BlogPostEditorPage />} />
          <Route path="blog/categories" element={<BlogCategoriesPage />} />
          <Route path="blog/tags" element={<BlogTagsPage />} />
          <Route path="blog/media" element={<BlogMediaPage />} />

          {/* Leader routes */}
          <Route path="leader/dashboard" element={<LeaderDashboardPage />} />
          <Route path="leader/voters" element={<VotersPage key="leader-voters" />} />
          <Route path="leader/voters/:id" element={<VoterDetailPage />} />

          {/* Digitador routes */}
          <Route path="digitador/dashboard" element={<DigitadorDashboardPage />} />
          <Route path="digitador/voters" element={<VotersPage key="digitador-voters" />} />
          <Route path="digitador/voters/:id" element={<VoterDetailPage />} />

          {/* Admin routes */}
          <Route path="admin/dashboard" element={<DashboardPage />} />
          <Route path="admin/clients" element={<div className="text-gray-500">Clientes - Em breve</div>} />
          <Route path="admin/plans" element={<div className="text-gray-500">Planos - Em breve</div>} />
          <Route path="admin/settings/users" element={<div className="text-gray-500">Usuários - Em breve</div>} />
          <Route path="admin/settings/general" element={<div className="text-gray-500">Configurações Gerais - Em breve</div>} />

          {/* Shared settings */}
          <Route path="settings/users" element={<UsersPage />} />
          <Route path="settings/general" element={<ConfiguracoesPage />} />
          <Route path="settings/voter-form" element={<VoterFormConfigPage />} />
          <Route path="settings/political" element={<PoliticalConfigPage />} />
          <Route path="settings/categories" element={<CategoriesPage />} />
          <Route path="settings/campaigns" element={<CampaignsPage />} />
          <Route path="settings/demands" element={<DemandSettingsPage />} />
          <Route path="settings/demands/:id" element={<DemandPipelineDetailPage />} />
          <Route path="settings/security" element={<div className="text-gray-500">Segurança - Em breve</div>} />
          <Route path="settings/integrations" element={<IntegrationsPage />} />
          <Route path="settings/app" element={<AppConfigPage />} />
          <Route path="settings/app/edit" element={<AppConfigFormPage />} />
          <Route path="settings/notifications" element={<PushNotificationsPage />} />
          <Route path="settings/integrations/docs" element={<ApiDocsPage />} />

          {/* Comunidade */}
          <Route path="community/dashboard" element={<CommunityDashboardPage />} />
          <Route path="community/missions" element={<CommunityMissionsPage />} />
          <Route path="community/arsenal" element={<CommunityArsenalPage />} />
          <Route path="community/channels" element={<CommunityChannelsPage />} />
          <Route path="community/channels/:channelId" element={<CommunityChannelChatPage />} />
          <Route path="community/members" element={<CommunityMembersPage />} />
          <Route path="community/moderation" element={<CommunityModerationPage />} />
          <Route path="community/settings" element={<CommunitySettingsPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  )
}

function App() {
  return (
    <AuthProvider>
      <LoadingProvider>
        <Router>
          <AppContent />
        </Router>
      </LoadingProvider>
    </AuthProvider>
  )
}

export default App
